import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    CheckCircle2, XCircle, Send, FileText, Mail, Printer, Save, AlertTriangle, Plus, Microscope, ShieldAlert, ArrowUp, ArrowDown, Minus, TrendingUp,
} from 'lucide-react';
import { toast } from 'react-toastify';
import LabServices from '../../shared/ApiServices/LabServices';
import { ProcessingSkeleton } from '../../shared/custom-ui/skeleton/Lab';
import { Select, Dropdown, Dialog, Button, TextArea } from '../../shared/custom-ui';
import useLabPermissions from './useLabPermissions';
import {
    extractLabResultsList, extractLabResultId, extractResultStatus, normalizeResultStatus,
    extractTestParametersList, extractLabTestsList, extractLabTestId, extractTestName,
    extractLabOrdersList, extractLabOrderId, extractLabSamplesList,
    normalizeSampleStatus, SAMPLE_STATUS_META,
    RESULT_STATUS_META, PARAM_FLAG_META,
    type LabResult, type LabTest, type LabResultParameter, type LabSample,
} from './labUtils';
import ParameterTrendChart from './ParameterTrendChart';
import ResultAuditTrail from './ResultAuditTrail';

import PathologyUploadModal from '../Pathology/PathologyUploadModal';

// ─── Email report modal ────────────────────────────────────────────────────────
const EmailModal: React.FC<{ orderId: string; onClose: () => void }> = ({ orderId, onClose }) => {
    const [email, setEmail] = useState('');
    const [sending, setSending] = useState(false);
    const handleSend = async () => {
        if (!email.trim()) { toast.error('Enter an email address'); return; }
        setSending(true);
        try {
            const res: any = await LabServices.emailReport(orderId, email.trim());
            toast.success(res?.message ?? 'Report emailed');
            onClose();
        }
        catch (err: any) { toast.error(err?.message ?? 'Failed to send'); } finally { setSending(false); }
    };
    return (
        <Dialog isOpen onClose={onClose} title="Email Report" size="small">
            <div className="flex flex-col gap-4 -mt-1">
                <input type="email" placeholder="patient@email.com"
                    className="w-full rounded-[var(--v2-radius-md)] border border-[var(--v2-border-glass-strong)] bg-[var(--v2-bg-card)] px-3.5 py-2.5 text-[14px] text-[var(--v2-text-primary)] outline-none focus:ring-[3px] focus:ring-[var(--v2-primary-500)]/18 focus:border-[var(--v2-primary-500)]"
                    value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSend()} />
                <div className="flex gap-3 justify-end">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" onClick={handleSend} loading={sending} leftIcon={!sending ? <Mail className="w-full h-full" strokeWidth={2} /> : undefined}>
                        Send
                    </Button>
                </div>
            </div>
        </Dialog>
    );
};

// ─── Rejection reason modal ────────────────────────────────────────────────────
const RejectModal: React.FC<{ onConfirm: (reason: string) => void; onClose: () => void }> = ({ onConfirm, onClose }) => {
    const [reason, setReason] = useState('');
    return (
        <Dialog isOpen onClose={onClose} size="small" preventCloseOnOverlayClick showCloseButton={false}>
            <div className="flex flex-col gap-4 -mt-2">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                        <XCircle className="w-5 h-5 text-red-600" strokeWidth={2.5} />
                    </div>
                    <div>
                        <h3 className="text-[15px] font-bold text-[var(--v2-text-heading)] m-0">Reject Results</h3>
                        <p className="text-[12px] font-medium text-[var(--v2-text-secondary)] m-0 leading-relaxed">Visible in the audit trail.</p>
                    </div>
                </div>
                <TextArea rows={3} placeholder="State the reason for rejection…" value={reason} onChange={e => setReason(e.target.value)} />
                <div className="flex gap-3 justify-end">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button variant="danger" onClick={() => onConfirm(reason)} disabled={!reason.trim()}>Reject</Button>
                </div>
            </div>
        </Dialog>
    );
};

// ─── Critical Acknowledge modal ────────────────────────────────────────────────────
const CriticalAckModal: React.FC<{ paramNames: string[]; onConfirm: (note: string) => void; onClose: () => void; saving: boolean }> = ({ paramNames, onConfirm, onClose, saving }) => {
    const [note, setNote] = useState('');
    return (
        <Dialog isOpen onClose={onClose} size="small" preventCloseOnOverlayClick showCloseButton={false}>
            <div className="flex flex-col gap-4 -mt-2">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                        <ShieldAlert className="w-5 h-5 text-red-600" strokeWidth={2.5} />
                    </div>
                    <h3 className="text-[15px] font-bold text-[var(--v2-text-heading)] m-0">Acknowledge Critical Value</h3>
                </div>
                <p className="text-[12px] font-medium text-[var(--v2-text-secondary)] m-0 leading-relaxed bg-red-50 p-2.5 rounded-lg border border-red-100">
                    Critical parameter{paramNames.length > 1 ? 's' : ''}: <strong className="text-red-700 font-bold">{paramNames.join(', ')}</strong>. Record who was notified before submitting.
                </p>
                <TextArea label="Notification Details" required rows={3} placeholder="e.g. Dr. Sharma notified by phone at 3:40pm" value={note} onChange={e => setNote(e.target.value)} />
                <div className="flex gap-3 justify-end">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button variant="danger" onClick={() => onConfirm(note)} disabled={!note.trim()} loading={saving} leftIcon={!saving ? <ShieldAlert className="w-full h-full" strokeWidth={2} /> : undefined}>
                        Acknowledge
                    </Button>
                </div>
            </div>
        </Dialog>
    );
};

// ─── Status flow sidebar component ────────────────────────────────────────────
const FLOW = ['draft', 'pending_approval', 'approved'] as const;
const ResultStatusFlow: React.FC<{ status: string }> = ({ status }) => (
    <div className="flex flex-wrap items-center gap-1.5">
        {FLOW.map((s, i) => {
            const meta = RESULT_STATUS_META[s];
            const isActive = status === s;
            const isPast = FLOW.indexOf(status as any) > i || status === 'approved';
            return (
                <React.Fragment key={s}>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ background: (isActive || isPast) ? meta.color : 'var(--v2-border-subtle)' }}>
                            {isPast && s !== status
                                ? <CheckCircle2 className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                                : <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-wide whitespace-nowrap" style={{ color: (isActive || isPast) ? meta.color : 'var(--v2-text-muted)' }}>
                            {meta.label}
                        </span>
                    </div>
                    {i < FLOW.length - 1 && (
                        <div className="w-4 sm:w-6 h-[2px] rounded-full flex-shrink-0" style={{ background: isPast && s !== status ? meta.color : 'var(--v2-border-subtle)' }} />
                    )}
                </React.Fragment>
            );
        })}
        {status === 'rejected' && (
            <div className="flex items-center gap-1.5 ml-2">
                <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#DC2626' }}>
                    <XCircle className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#DC2626' }}>Rejected</span>
            </div>
        )}
    </div>
);

// ─── Main TestProcessing ───────────────────────────────────────────────────────
import { type LabOrder } from './labUtils';

interface TestProcessingProps {
    orderId?: string;
    order?: LabOrder;
    /** Pre-fetched by a parent (e.g. OrderDetail) — when supplied, skips this component's own getSamplesForOrder() call. */
    samples?: LabSample[];
    /** Pre-fetched lab test catalog — when supplied, skips this component's own getLabTests() call. */
    allTests?: LabTest[];
}

export const TestProcessing: React.FC<TestProcessingProps> = ({ orderId: orderIdProp, order, samples: samplesProp, allTests: allTestsProp }) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const orderId = orderIdProp ?? searchParams.get('orderId') ?? '';
    const { canEnterResults, canApproveReports, canDispatchReports, canVerifyResults, currentUser } = useLabPermissions();

    const [results, setResults] = useState<LabResult[]>([]);
    const [fetchedAllTests, setFetchedAllTests] = useState<LabTest[]>([]);
    const [fetchedSamples, setFetchedSamples] = useState<LabSample[]>([]);
    const allTests = allTestsProp ?? fetchedAllTests;
    const samples = samplesProp ?? fetchedSamples;
    const [loading, setLoading] = useState(false);
    const [selectedResult, setSelectedResult] = useState<LabResult | null>(null);
    const [isNewEntry, setIsNewEntry] = useState(false);
    const [selectedTestId, setSelectedTestId] = useState('');
    const [parameters, setParameters] = useState<LabResultParameter[]>([]);
    const [findings, setFindings] = useState('');
    const [impression, setImpression] = useState('');
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [approving, setApproving] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [loadingParams, setLoadingParams] = useState(false);
    const [isReportDropdownOpen, setIsReportDropdownOpen] = useState(false);
    const [isSelectTestModalOpen, setIsSelectTestModalOpen] = useState(false);
    const mountedRef = useRef(true);

    // ── Delta check — patient's prior result for the same test ──────────────────
    const [fetchedOrderMobile, setFetchedOrderMobile] = useState('');
    const orderMobile = order ? (order.mobile ?? '') : fetchedOrderMobile;
    const [fetchedOrderTests, setFetchedOrderTests] = useState<any[]>([]);
    const [priorParams, setPriorParams] = useState<LabResultParameter[]>([]);

    // ── Critical-value acknowledgment ────────────────────────────────────────────
    const [showCriticalAckModal, setShowCriticalAckModal] = useState(false);
    const [acknowledging, setAcknowledging] = useState(false);
    const [criticalAckDoneLocal, setCriticalAckDoneLocal] = useState(false);

    // ── Client-enforced verification checkpoint ──────────────────────────────────
    const [verifying, setVerifying] = useState(false);

    // ── Parameter trend chart ────────────────────────────────────────────────────
    const [trendParam, setTrendParam] = useState<string | null>(null);

    const loadData = useCallback(async () => {
        if (!orderId) return;
        setLoading(true);
        try {
            const [r, t, sm] = await Promise.all([
                LabServices.getOrderResults(orderId),
                allTestsProp ? Promise.resolve(null) : LabServices.getLabTests(),
                samplesProp ? Promise.resolve(null) : LabServices.getSamplesForOrder(orderId).catch(() => []),
            ]);
            if (mountedRef.current) {
                setResults(extractLabResultsList(r));
                if (!allTestsProp) setFetchedAllTests(extractLabTestsList(t));
                if (!samplesProp) setFetchedSamples(extractLabSamplesList(sm));
            }
        } catch (err: any) { toast.error(err?.message ?? 'Failed to load results'); }
        finally { if (mountedRef.current) setLoading(false); }
    }, [orderId, allTestsProp, samplesProp]);

    const sampleGateFor = useCallback((testId: string): { sample: LabSample | null; status: ReturnType<typeof normalizeSampleStatus> | null } => {
        const sample = samples.find(sm => (sm.testId ?? (sm as any).test_id) === testId) ?? null;
        if (!sample) return { sample: null, status: null };
        return { sample, status: normalizeSampleStatus(sample) };
    }, [samples]);

    useEffect(() => { mountedRef.current = true; loadData(); return () => { mountedRef.current = false; }; }, [loadData]);

    useEffect(() => {
        if (!orderId || order) return;
        LabServices.getOrder(orderId)
            .then((res: any) => {
                const oData = res?.order ?? res;
                setFetchedOrderMobile(oData?.mobile ?? '');
                const topLevelTests: any[] = res?.tests ?? [];
                const orderTestsRaw: any[] = oData?.tests ?? [];
                const seen = new Set<string>();
                const merged = [
                    ...orderTestsRaw.map((t: any) => { seen.add(t.testId ?? t.test_id ?? ''); return t; }),
                    ...topLevelTests
                        .map((t: any) => ({ testId: t.testId ?? t.test_id ?? String(t.SK ?? '').replace(/^TEST#/, ''), testName: t.testName ?? t.test_name }))
                        .filter((t: any) => t.testId && !seen.has(t.testId)),
                ];
                setFetchedOrderTests(merged);
            })
            .catch(() => { });
    }, [orderId, order]);

    useEffect(() => {
        let cancelled = false;
        if (!orderMobile || !selectedTestId) { setPriorParams([]); return; }
        (async () => {
            try {
                const res = await LabServices.listOrders({ mobile: orderMobile, status: 'completed', limit: 10 });
                const priorOrders = extractLabOrdersList(res)
                    .filter(o => extractLabOrderId(o) !== orderId)
                    .sort((a, b) => new Date(b.orderDate ?? b.createdAt ?? (b as any).created_at ?? 0).getTime()
                        - new Date(a.orderDate ?? a.createdAt ?? (a as any).created_at ?? 0).getTime())
                    .slice(0, 5);
                for (const po of priorOrders) {
                    if (cancelled) return;
                    const priorResults = extractLabResultsList(await LabServices.getOrderResults(extractLabOrderId(po)));
                    const match = priorResults.find(r => (r.testId ?? (r as any).test_id) === selectedTestId && (r.parameters?.length ?? 0) > 0);
                    if (match) { if (!cancelled) setPriorParams(match.parameters ?? []); return; }
                }
                if (!cancelled) setPriorParams([]);
            } catch { if (!cancelled) setPriorParams([]); }
        })();
        return () => { cancelled = true; };
    }, [orderMobile, selectedTestId, orderId]);

    const selectResult = useCallback(async (result: LabResult) => {
        setSelectedResult(result);
        setIsNewEntry(false);
        setFindings(result.findings ?? '');
        setImpression(result.impression ?? '');
        setCriticalAckDoneLocal(false);
        const resultTestId = result.testId ?? (result as any).test_id ?? '';
        setSelectedTestId(resultTestId);
        try {
            const templateParams = extractTestParametersList(await LabServices.getTestParameters(resultTestId));
            const existingParams = result.parameters ?? [];
            const merged = templateParams.length > 0
                ? templateParams.map(tp => {
                    const existing = existingParams.find(ep => (ep.paramName ?? (ep as any).param_name) === (tp.paramName ?? (tp as any).param_name));
                    return existing ? { ...tp, value: existing.value ?? '' } : { ...tp, value: '' };
                })
                : existingParams;
            setParameters(merged);
        } catch { setParameters(result.parameters ?? []); }
    }, []);

    // 4.3 — Get Single Test Result Details: refresh selectedResult from server after any mutation
    const refreshSelectedResult = async (rId: string) => {
        if (!rId || !orderId) return;
        try {
            const fresh = await LabServices.getResult(orderId, rId);
            // API may return { result: {...} } or the object directly
            const obj = fresh?.result ?? fresh;
            if (obj && (obj.resultId ?? obj.SK ?? obj.result_id)) {
                setSelectedResult(obj as any);
            }
        } catch { /* non-fatal — keep existing state */ }
    };

    const handleTestSelect = async (testId: string) => {
        setSelectedTestId(testId);
        if (!testId) { setParameters([]); return; }
        setLoadingParams(true);
        try {
            const params = extractTestParametersList(await LabServices.getTestParameters(testId));
            setParameters(params);
        } catch { setParameters([]); }
        finally { setLoadingParams(false); }
    };

    const buildResultPayload = (extra: Record<string, any> = {}) => ({
        testId: selectedTestId,
        findings, impression,
        parameters: parameters.map(p => ({
            paramName: p.paramName ?? (p as any).param_name ?? '',
            value: p.value ?? '',
            unit: p.unit ?? '',
            referenceRange: p.referenceRange ?? (p as any).reference_range ?? '',
            criticalLow: p.criticalLow,
            criticalHigh: p.criticalHigh,
        })),
        ...extra,
    });

    const handleSaveDraft = async () => {
        if (!orderId || !selectedTestId) { toast.error('Select a test first'); return; }
        setSaving(true);
        try {
            const payload = buildResultPayload({ enteredBy: currentUser?.name ?? currentUser?.id ?? undefined });
            if (selectedResult && extractLabResultId(selectedResult)) {
                const rId = extractLabResultId(selectedResult);
                const res: any = await LabServices.updateResult(rId, payload);
                toast.success(res?.message ?? 'Result updated');
                await refreshSelectedResult(rId);
            } else {
                const res = await LabServices.saveDraftResult(orderId, payload);
                const newResultId = res?.resultId ?? res?.result_id ?? res?.SK;
                if (newResultId) {
                    setSelectedResult({ ...payload, resultId: newResultId, reportStatus: 'draft', status: 'draft' } as any);
                    await refreshSelectedResult(newResultId);
                }
                toast.success(res?.message ?? 'Draft saved');
            }
            loadData();
        } catch (err: any) { toast.error(err?.message ?? 'Save failed'); } finally { setSaving(false); }
    };

    const handleAcknowledgeCritical = async (note: string) => {
        if (!selectedResult || !extractLabResultId(selectedResult)) { toast.error('Save draft first'); return; }
        setAcknowledging(true);
        try {
            const ackBy = currentUser?.name ?? currentUser?.id ?? 'Unknown user';
            const ackAt = new Date().toISOString();
            const payload = buildResultPayload({ criticalAcknowledgedBy: ackBy, criticalAcknowledgedAt: ackAt, criticalNote: note });
            const res: any = await LabServices.updateResult(extractLabResultId(selectedResult), payload);
            toast.success(res?.message ?? 'Critical value acknowledged');
            setSelectedResult(prev => prev ? { ...prev, criticalAcknowledgedBy: ackBy, criticalAcknowledgedAt: ackAt, criticalNote: note } as any : prev);
            setCriticalAckDoneLocal(true);
            setShowCriticalAckModal(false);
        } catch (err: any) { toast.error(err?.message ?? 'Failed to acknowledge'); } finally { setAcknowledging(false); }
    };

    const handleVerify = async () => {
        if (!selectedResult) return;
        const rId = extractLabResultId(selectedResult);
        if (!rId) return;
        setVerifying(true);
        try {
            const verifiedBy = currentUser?.name ?? currentUser?.id ?? 'Unknown user';
            const verifiedAt = new Date().toISOString();
            const payload = buildResultPayload({ verifiedBy, verifiedAt });
            const res: any = await LabServices.updateResult(rId, payload);
            toast.success(res?.message ?? 'Result verified');
            setSelectedResult(prev => prev ? { ...prev, verifiedBy, verifiedAt } as any : prev);
        } catch (err: any) { toast.error(err?.message ?? 'Verify failed'); } finally { setVerifying(false); }
    };

    const handleSubmitForReview = async () => {
        if (!selectedResult) return;
        const rId = extractLabResultId(selectedResult);
        if (!rId) { toast.error('Save draft first'); return; }
        setSubmitting(true);
        try {
            const res = await LabServices.submitForReview(rId);
            toast.success(res?.message ?? 'Submitted for approval');
            await refreshSelectedResult(rId);
            loadData();
        } catch (err: any) { toast.error(err?.message ?? 'Submit failed'); } finally { setSubmitting(false); }
    };

    const handleApprove = async () => {
        if (!selectedResult) return;
        const rId = extractLabResultId(selectedResult);
        if (!rId) return;
        setApproving(true);
        try {
            const res = await LabServices.approveReport(rId);
            toast.success(res?.message ?? 'Report approved');
            await refreshSelectedResult(rId);
            loadData();
        } catch (err: any) { toast.error(err?.message ?? 'Approve failed'); } finally { setApproving(false); }
    };

    const handleReject = async (reason: string) => {
        if (!selectedResult) return;
        const rId = extractLabResultId(selectedResult);
        setApproving(true);
        try {
            const res: any = await LabServices.rejectReport(rId, reason);
            toast.success(res?.message ?? 'Report rejected');
            setShowRejectModal(false);
            await refreshSelectedResult(rId);
            loadData();
        } catch (err: any) { toast.error(err?.message ?? 'Reject failed'); } finally { setApproving(false); }
    };

    const handleGeneratePdf = async () => {
        if (!orderId) return;
        setGeneratingPdf(true);
        try {
            const genRes: any = await LabServices.generateReportPdf(orderId);

            let openUrl = '';
            try {
                const getRes: any = await LabServices.getReportPdfUrl(orderId);
                openUrl = getRes?.reportUrl ?? getRes?.report_url ?? getRes?.url ?? getRes?.presignedUrl ?? '';
            } catch {

            }

            const isValidUrl = (u: string) => u.startsWith('http://') || u.startsWith('https://');

            if (openUrl && isValidUrl(openUrl)) {
                window.open(openUrl, '_blank', 'noopener,noreferrer');
                toast.success(genRes?.message ?? 'Report generated — opening in new tab');
            } else {
                const reportKey: string = genRes?.reportKey ?? genRes?.report_key ?? '';
                if (reportKey) {
                    toast.success(`${genRes?.message ?? 'Report generated'} — refresh and try again shortly.`);
                } else {
                    toast.info(genRes?.message ?? 'Report generated. Try opening it again in a moment.');
                }
            }
        } catch (err: any) { toast.error(err?.message ?? 'Failed to generate report'); } finally { setGeneratingPdf(false); }
    };

    const handlePrint = async () => {
        if (!orderId) return;
        setPrinting(true);
        try {
            const res: any = await LabServices.getReportPdfUrl(orderId);
            const url: string = res?.reportUrl ?? res?.report_url ?? res?.url ?? res?.presignedUrl ?? '';
            if (!url?.startsWith('http')) { toast.info('Generate the report first, then try printing.'); return; }
            // Fetch as blob → hidden iframe → print() — avoids popup blocker entirely
            const blob = await fetch(url).then(r => r.blob());
            const blobUrl = URL.createObjectURL(blob);
            const iframe = document.createElement('iframe');
            iframe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;border:0;';
            iframe.src = blobUrl;
            document.body.appendChild(iframe);
            iframe.onload = () => {
                iframe.contentWindow?.print();
                setTimeout(() => { document.body.removeChild(iframe); URL.revokeObjectURL(blobUrl); }, 1000);
            };
        } catch (err: any) { toast.error(err?.message ?? 'Print failed'); } finally { setPrinting(false); }
    };

    if (!orderId) {
        return (
            <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: 'var(--v2-primary-50, rgba(4,94,184,0.1))' }}>
                    <Microscope className="w-7 h-7" style={{ color: 'var(--v2-primary-500)' }} strokeWidth={1.5} />
                </div>
                <div className="flex flex-col gap-1">
                    <p className="text-[14px] font-bold text-[var(--v2-text-heading)] m-0">Select an Order First</p>
                    <p className="text-[12px] text-[var(--v2-text-muted)] max-w-xs m-0">
                        Test processing is linked to a specific order.
                        Open an order and click <strong>"Enter Results"</strong> to get started.
                    </p>
                </div>
                <Button variant="primary" size="small" onClick={() => navigate('/lab/orders')} leftIcon={<Microscope className="w-full h-full" strokeWidth={2} />}>
                    Go to Orders
                </Button>
            </div>
        );
    }

    if (loading) return <ProcessingSkeleton />;

    const currentStatus = selectedResult
        ? normalizeResultStatus((selectedResult as any).reportStatus ?? selectedResult.status)
        : 'draft';
    const statusMeta = RESULT_STATUS_META[currentStatus];
    const isApproved = currentStatus === 'approved';
    const isRejected = currentStatus === 'rejected';
    const canEdit = !isApproved && !isRejected && canEnterResults;
    const canApproveNow = currentStatus === 'pending_approval' && canApproveReports;

    const orderTestEntries = order?.tests ?? fetchedOrderTests;
    const catalogById = new Map(allTests.map(ct => [extractLabTestId(ct), ct]));
    const testOptions = orderTestEntries.length > 0
        ? orderTestEntries.map((t: any) => {
            const tid = t.testId ?? t.test_id ?? '';
            const name = t.testName ?? t.test_name ?? (catalogById.has(tid) ? extractTestName(catalogById.get(tid)!) : tid);
            return { value: tid, label: name };
        }).filter(o => o.value)
        : allTests.map(t => ({ value: extractLabTestId(t), label: extractTestName(t) }));

    const parseNum = (v?: string) => { const n = Number(v); return v != null && v !== '' && !isNaN(n) ? n : null; };
    const isParamCritical = (p: LabResultParameter): boolean => {
        const val = parseNum(p.value);
        if (val === null) return false;
        const lo = parseNum(p.criticalLow);
        const hi = parseNum(p.criticalHigh);
        return (lo !== null && val < lo) || (hi !== null && val > hi);
    };
    const criticalParams = parameters.filter(isParamCritical);
    const hasCritical = criticalParams.length > 0;
    const isCriticalAcknowledged = criticalAckDoneLocal || Boolean((selectedResult as any)?.criticalAcknowledgedBy);

    const enteredByUser = (selectedResult as any)?.enteredBy as string | undefined;
    const verifiedByUser = (selectedResult as any)?.verifiedBy as string | undefined;
    const isSelfVerify = Boolean(enteredByUser && currentUser && (enteredByUser === currentUser.name || enteredByUser === currentUser.id));

    const activeTestId = selectedTestId || (selectedResult as any)?.testId || (selectedResult as any)?.test_id || '';
    const { sample: gatedSample, status: gatedStatus } = sampleGateFor(activeTestId);
    const sampleRejectsEntry = gatedStatus === 'rejected';

    return (
        <>
            <div className="flex-1 rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] flex flex-col overflow-hidden min-h-[600px]">
                {/* ── HEADER ── */}
                <div className="border-b border-[var(--v2-border-subtle)] bg-[var(--v2-bg-surface)]">

                    <div className="px-4 py-2.5 border-b border-[var(--v2-border-subtle)] flex flex-col md:flex-row md:items-center justify-between gap-2">
                        {/* LEFT: Select existing report & Meta */}
                        <div className="flex items-center gap-3 flex-wrap min-w-0">
                            {results.length === 0 && !canEnterResults && (
                                <div className="text-[12px] font-medium text-[var(--v2-text-muted)] italic px-2">No results found.</div>
                            )}
                            {(results.length > 0 || isNewEntry) && (() => {
                                const activeValue = !isNewEntry && selectedResult ? extractLabResultId(selectedResult) : 'new_entry';
                                const activeOption = results.find(r => extractLabResultId(r) === activeValue);
                                const activeOptionLabel = activeValue === 'new_entry'
                                    ? 'New Result Entry (Draft)'
                                    : activeOption ? (((activeOption as any).testName || (activeOption as any).test_name || '').trim() || activeOption.testId || (activeOption as any).test_id || 'Result') : 'Select report';

                                return (
                                    <div className="flex items-center gap-2">
                                        <Dropdown
                                            isOpen={isReportDropdownOpen}
                                            toggleDropdown={() => setIsReportDropdownOpen(!isReportDropdownOpen)}
                                            onClose={() => setIsReportDropdownOpen(false)}
                                            buttonLabel={activeOptionLabel}
                                            variant="outlined"
                                            size="small"
                                            options={[
                                                ...results.map(r => {
                                                    const rId = extractLabResultId(r);
                                                    const displayName = ((r as any).testName || (r as any).test_name || '').trim() || r.testId || (r as any).test_id || 'Result';
                                                    const rStatus = normalizeResultStatus((r as any).reportStatus ?? r.status);
                                                    return {
                                                        value: rId,
                                                        label: displayName,
                                                        icon: <div className="w-2 h-2 rounded-full mt-1.5" style={{ background: RESULT_STATUS_META[rStatus].color }} />,
                                                        onClick: () => {
                                                            selectResult(r);
                                                            setIsReportDropdownOpen(false);
                                                        }
                                                    };
                                                }),
                                                ...(isNewEntry ? [{
                                                    value: 'new_entry',
                                                    label: 'New Result Entry (Draft)',
                                                    icon: <Plus className="w-3.5 h-3.5 mt-0.5 text-[var(--v2-text-muted)]" />,
                                                    onClick: () => {
                                                        setIsReportDropdownOpen(false);
                                                        setIsNewEntry(true);
                                                        setSelectedResult(null);
                                                        if (!selectedTestId) setIsSelectTestModalOpen(true);
                                                    }
                                                }] : [])
                                            ]}
                                        />
                                        {!isNewEntry && selectedResult && (
                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide flex-shrink-0" style={{ background: statusMeta.bg, color: statusMeta.color, border: `1px solid ${statusMeta.color}30` }}>
                                                {statusMeta.label}
                                            </span>
                                        )}
                                    </div>
                                );
                            })()}

                            {/* Change Test button */}
                            {isNewEntry && canEnterResults && selectedTestId && (
                                <button type="button" onClick={() => setIsSelectTestModalOpen(true)} className="px-2.5 py-1.5 rounded-lg bg-transparent border border-[var(--v2-border-interactive)] text-[11px] font-bold text-[var(--v2-text-secondary)] hover:text-[var(--v2-text-primary)] hover:bg-[var(--v2-bg-hover)] transition-colors">
                                    Change Test
                                </button>
                            )}
                        </div>

                        {/* RIGHT: Actions */}
                        <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                            {/* Upload Slides */}
                            <div className="scale-90 origin-right">
                                <PathologyUploadModal
                                    onUploadFinished={() => { loadData(); }}
                                    preselectedPatientId={order?.patientId ? (order.patientId.startsWith('PATIENT#') ? order.patientId : `PATIENT#${order.patientId}`) : undefined}
                                    preselectedPatientName={order?.patientName ?? ((order?.patientFirstName || order?.patientLastName) ? `${order.patientFirstName ?? ''} ${order.patientLastName ?? ''}`.trim() : undefined)}
                                    preselectedOrderId={orderId}
                                />
                            </div>

                            {/* New Result Entry Button */}
                            {canEnterResults && !isNewEntry && (
                                <Button variant="outline" size="small" onClick={() => setIsSelectTestModalOpen(true)} leftIcon={<Plus className="w-full h-full" strokeWidth={2.5} />}
                                    className="!text-[var(--v2-primary-600,#045eb8)] !border-[var(--v2-primary-400,rgba(4,94,184,0.35))]">
                                    New Entry
                                </Button>
                            )}

                            {/* Workflow Actions */}
                            {canEdit && (selectedResult || (isNewEntry && selectedTestId)) && (
                                <>
                                    <Button variant="outline" size="small" onClick={handleSaveDraft} loading={saving} disabled={sampleRejectsEntry}
                                        title={sampleRejectsEntry ? 'Cannot save results against a rejected specimen' : undefined}
                                        leftIcon={!saving ? <Save className="w-full h-full" strokeWidth={2} /> : undefined}>
                                        Save
                                    </Button>
                                    <Button variant="primary" size="small" onClick={handleSubmitForReview}
                                        disabled={!selectedResult || !extractLabResultId(selectedResult) || (hasCritical && !isCriticalAcknowledged) || sampleRejectsEntry}
                                        loading={submitting}
                                        title={sampleRejectsEntry ? 'Cannot submit results against a rejected specimen' : (hasCritical && !isCriticalAcknowledged ? 'Acknowledge the critical value above before submitting' : undefined)}
                                        leftIcon={!submitting ? <Send className="w-full h-full" strokeWidth={2} /> : undefined}>
                                        Submit
                                    </Button>
                                </>
                            )}
                            {canApproveNow && (
                                <>
                                    <Button variant="outline" size="small" onClick={() => setShowRejectModal(true)} disabled={approving}
                                        className="!text-red-600 !border-red-200 !bg-red-50 hover:!bg-red-600 hover:!text-white"
                                        leftIcon={<XCircle className="w-full h-full" strokeWidth={2} />}>
                                        Reject
                                    </Button>
                                    <Button variant="primary" size="small" onClick={handleApprove} loading={approving}
                                        style={{ background: 'linear-gradient(to bottom, #10b981, #059669)' }}
                                        leftIcon={!approving ? <CheckCircle2 className="w-full h-full" strokeWidth={2.5} /> : undefined}>
                                        Approve
                                    </Button>
                                </>
                            )}

                            {/* Stepper on top right */}
                            {selectedResult && !isNewEntry && (
                                <div className="hidden xl:block ml-1 scale-[0.70] origin-right opacity-80">
                                    <ResultStatusFlow status={currentStatus} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-5 bg-[var(--v2-bg-base)]">
                    {!selectedResult && !isNewEntry && (
                        <div className="flex-1 flex flex-col items-center justify-center text-center py-20">
                            <div className="w-16 h-16 rounded-full bg-[var(--v2-bg-surface)] flex items-center justify-center mb-4 border border-[var(--v2-border-card)]">
                                <FileText className="w-8 h-8 text-[var(--v2-text-muted)] opacity-50" strokeWidth={1.5} />
                            </div>
                            <h3 className="text-[15px] font-bold text-[var(--v2-text-heading)] m-0 mb-2">Select a Result</h3>
                            <p className="text-[13px] text-[var(--v2-text-secondary)] max-w-sm mx-auto m-0 leading-relaxed text-center">Choose an existing result from the top navigation to review or create a new entry.</p>
                        </div>
                    )}

                    {isNewEntry && !selectedTestId ? (
                        <div className="flex-1 flex flex-col items-center justify-center py-20 px-4 text-center">
                            <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4" style={{ background: 'var(--v2-primary-50, rgba(4,94,184,0.1))', color: 'var(--v2-primary-500)' }}>
                                <Microscope className="w-7 h-7" strokeWidth={2} />
                            </div>
                            <h3 className="text-[16px] font-bold text-[var(--v2-text-heading)] m-0 mb-2">Record New Findings</h3>
                            <p className="text-[13px] text-[var(--v2-text-secondary)] max-w-sm m-0 mb-5">Select a test from the catalog to load its parameters and begin recording results.</p>
                            <Button variant="primary" onClick={() => setIsSelectTestModalOpen(true)}>Select Test</Button>
                        </div>
                    ) : (
                        <>
                            {loadingParams && (
                                <div className="flex flex-col items-center justify-center h-full gap-3">
                                    <div className="w-7 h-7 rounded-full border-[3px] border-[var(--v2-primary-200,rgba(4,94,184,0.2))] border-t-[var(--v2-primary-500)] animate-spin" />
                                    <span className="text-[12px] font-bold text-[var(--v2-text-muted)]">Loading test parameters…</span>
                                </div>
                            )}

                            {/* Verification Warning */}
                            {!loadingParams && currentStatus === 'draft' && selectedResult && extractLabResultId(selectedResult) && canVerifyResults && (
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
                                    <div className="flex items-center gap-2.5">
                                        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 text-amber-600">
                                            <ShieldAlert className="w-4 h-4" strokeWidth={2} />
                                        </div>
                                        <div className="flex flex-col gap-0">
                                            <h4 className="text-[12px] font-bold text-amber-800 m-0">Verification Required</h4>
                                            <p className="text-[11px] font-medium text-amber-700 m-0">This entry is a draft and should be verified before submission.</p>
                                        </div>
                                    </div>
                                    {verifiedByUser ? (
                                        <span className="inline-flex items-center gap-1.5 text-[12px] font-bold px-3 py-1.5 rounded-lg bg-green-100 text-green-700 border border-green-200 whitespace-nowrap">
                                            <CheckCircle2 className="w-4 h-4" strokeWidth={2.5} /> Verified by {verifiedByUser}
                                        </span>
                                    ) : (
                                        <div className="flex flex-col sm:items-end gap-1">
                                            <Button variant="outline" size="small" onClick={handleVerify} loading={verifying}
                                                className="!border-amber-300 !text-amber-800 !bg-amber-100 hover:!bg-amber-200"
                                                leftIcon={!verifying ? <CheckCircle2 className="w-full h-full" strokeWidth={2} /> : undefined}>
                                                Verify Entry
                                            </Button>
                                            {isSelfVerify && <span className="text-[10px] text-amber-600 font-medium">Self-verification discouraged</span>}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Only actionable before the report is finalized — once approved there's nothing left to confirm. */}
                            {!loadingParams && gatedStatus && gatedStatus !== 'accessioned' && !isApproved && (
                                <div className="rounded-xl p-3 flex items-start sm:items-center gap-2.5 border" style={{
                                    background: sampleRejectsEntry ? 'rgba(220,38,38,0.05)' : 'rgba(217,119,6,0.05)',
                                    borderColor: sampleRejectsEntry ? 'rgba(220,38,38,0.2)' : 'rgba(217,119,6,0.2)',
                                }}>
                                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: sampleRejectsEntry ? 'rgba(220,38,38,0.1)' : 'rgba(217,119,6,0.1)' }}>
                                        <AlertTriangle className="w-4 h-4" style={{ color: sampleRejectsEntry ? '#DC2626' : '#D97706' }} strokeWidth={2} />
                                    </div>
                                    <div className="flex flex-col gap-0">
                                        <h4 className="text-[12px] font-bold m-0" style={{ color: sampleRejectsEntry ? '#B91C1C' : '#B45309' }}>Accessioning Warning</h4>
                                        <p className="text-[11px] font-medium m-0 leading-snug" style={{ color: sampleRejectsEntry ? '#DC2626' : '#D97706' }}>
                                            {sampleRejectsEntry
                                                ? `Specimen rejected${gatedSample?.rejectionReason ? ` — ${gatedSample.rejectionReason}` : ''}. Results cannot be entered against a rejected sample.`
                                                : `Specimen not yet accessioned by the lab (currently: ${SAMPLE_STATUS_META[gatedStatus].label}). Confirm receipt before finalizing results.`}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {!loadingParams && hasCritical && (
                                <div className="rounded-xl p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border bg-red-50 border-red-200">
                                    <div className="flex items-center gap-2.5">
                                        <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                                            <ShieldAlert className="w-4 h-4 text-red-600" strokeWidth={2} />
                                        </div>
                                        <div className="flex flex-col gap-0">
                                            <h4 className="text-[12px] font-bold text-red-700 m-0">Critical Value Detected</h4>
                                            <p className="text-[11px] font-medium text-red-600 m-0">Parameter{criticalParams.length > 1 ? 's' : ''}: {criticalParams.map(p => p.paramName ?? (p as any).param_name).join(', ')}</p>
                                        </div>
                                    </div>
                                    {isCriticalAcknowledged ? (
                                        <span className="inline-flex items-center gap-1.5 text-[12px] font-bold px-3 py-1.5 rounded-lg bg-green-100 text-green-700 border border-green-200">
                                            <CheckCircle2 className="w-4 h-4" strokeWidth={2.5} /> Acknowledged
                                        </span>
                                    ) : canEdit && (
                                        <Button variant="danger" size="small" onClick={() => setShowCriticalAckModal(true)} leftIcon={<ShieldAlert className="w-full h-full" strokeWidth={2} />}>
                                            Acknowledge Now
                                        </Button>
                                    )}
                                </div>
                            )}

                            {!loadingParams && parameters.length > 0 && (
                                <div className="rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] overflow-hidden flex flex-col">
                                    <div className="px-4 py-2.5 border-b border-[var(--v2-border-subtle)] bg-[var(--v2-bg-surface)] flex items-center gap-2">
                                        <Microscope className="w-4 h-4" style={{ color: 'var(--v2-primary-500)' }} />
                                        <h4 className="text-[12px] font-bold text-[var(--v2-text-heading)] m-0">Clinical Parameters</h4>
                                    </div>
                                    <div className="overflow-x-auto w-full">
                                        <table className="w-full text-left min-w-[700px]">
                                            <thead>
                                                <tr className="border-b border-[var(--v2-border-subtle)]">
                                                    {['Parameter', 'Result Value', 'Unit', 'Ref. Range', 'Prior', 'Flag', ''].map(h => (
                                                        <th key={h} className="px-4 py-2.5 font-bold text-[var(--v2-text-secondary)] text-[10px] uppercase tracking-wide whitespace-nowrap bg-[var(--v2-bg-surface)]">{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-[var(--v2-border-subtle)]">
                                                {parameters.map((p, i) => {
                                                    const apiFlag = (p.flag ?? '').toUpperCase();
                                                    const value = Number(p.value ?? 0);
                                                    const refRange = p.referenceRange ?? (p as any).reference_range ?? '';
                                                    const [lo, hi] = refRange.split('-').map((s: string) => Number(s.trim()));
                                                    const derivedAbnormal = refRange && !isNaN(lo) && !isNaN(hi) && value !== 0 && (value < lo || value > hi);
                                                    const critical = isParamCritical(p);
                                                    const flagKey = critical ? 'C' : (apiFlag || (derivedAbnormal ? (value > hi ? 'H' : 'L') : 'N'));
                                                    const flagMeta = PARAM_FLAG_META[flagKey] ?? PARAM_FLAG_META['N'];
                                                    const isAbnormal = flagKey !== 'N';
                                                    const paramName = p.paramName ?? (p as any).param_name ?? '';
                                                    const priorMatch = priorParams.find(pp => (pp.paramName ?? (pp as any).param_name) === paramName);
                                                    const priorVal = priorMatch ? parseNum(priorMatch.value) : null;
                                                    const curVal = parseNum(p.value);
                                                    return (
                                                        <tr key={i} className={`transition-colors hover:bg-[var(--v2-bg-surface)] ${critical ? 'bg-red-50/30' : isAbnormal ? 'bg-orange-50/30' : ''}`}>
                                                            <td className="px-4 py-2.5">
                                                                <span className="text-[12px] font-bold text-[var(--v2-text-primary)]">{paramName || 'Unnamed Parameter'}</span>
                                                            </td>
                                                            <td className="px-4 py-2 w-36">
                                                                {canEdit ? (
                                                                    <input type="text"
                                                                        className={`w-full rounded-lg border px-3 py-1.5 text-[13px] font-bold outline-none transition-colors ${critical ? 'border-red-300 text-red-700 focus:border-red-500 focus:ring-2 focus:ring-red-200' : isAbnormal ? 'border-orange-300 text-orange-700 focus:border-orange-500 focus:ring-2 focus:ring-orange-200' : 'border-[var(--v2-border-interactive)] text-[var(--v2-text-primary)] focus:border-[var(--v2-primary-500)] focus:ring-2 focus:ring-[var(--v2-primary-500)]/20'}`}
                                                                        value={p.value ?? ''}
                                                                        placeholder="Enter value"
                                                                        onChange={e => setParameters(ps => ps.map((r, j) => j === i ? { ...r, value: e.target.value } : r))}
                                                                    />
                                                                ) : (
                                                                    <span className={`text-[14px] font-bold ${critical ? 'text-red-600' : isAbnormal ? 'text-orange-600' : 'text-[var(--v2-text-primary)]'}`}>{p.value ?? '—'}</span>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-2.5 text-[var(--v2-text-secondary)] font-medium text-[11px]">{p.unit || '—'}</td>
                                                            <td className="px-4 py-2.5 text-[var(--v2-text-muted)] font-mono text-[10px] whitespace-nowrap">{refRange || '—'}</td>
                                                            <td className="px-4 py-2.5">
                                                                {priorVal !== null && curVal !== null ? (
                                                                    <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--v2-bg-surface)] w-fit border border-[var(--v2-border-subtle)]" title={`Previous visit: ${priorMatch?.value}`}>
                                                                        {curVal > priorVal ? <ArrowUp className="w-3 h-3 text-red-500" strokeWidth={3} />
                                                                            : curVal < priorVal ? <ArrowDown className="w-3 h-3 text-blue-500" strokeWidth={3} />
                                                                                : <Minus className="w-3 h-3 text-[var(--v2-text-muted)]" strokeWidth={3} />}
                                                                        <span className="text-[10px] font-bold text-[var(--v2-text-secondary)]">{priorMatch?.value}</span>
                                                                    </div>
                                                                ) : <span className="text-[10px] text-[var(--v2-text-muted)]">—</span>}
                                                            </td>
                                                            <td className="px-4 py-2.5">
                                                                <span className="inline-flex items-center text-[9px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wide"
                                                                    style={{ background: flagMeta.bg, color: flagMeta.color }}>
                                                                    {flagMeta.label}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-2.5 text-right">
                                                                <button type="button" title="View trend chart" onClick={() => setTrendParam(paramName)}
                                                                    className="w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer border-0 bg-transparent text-[var(--v2-text-muted)] hover:bg-blue-50 hover:text-blue-600 transition-colors">
                                                                    <TrendingUp className="w-3.5 h-3.5" strokeWidth={2} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {(selectedResult || (isNewEntry && selectedTestId)) && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <TextArea
                                        label="Findings"
                                        rows={3} value={findings} onChange={e => setFindings(e.target.value)} readOnly={!canEdit}
                                        placeholder="Enter detailed clinical findings..." />
                                    <TextArea
                                        label="Impression"
                                        rows={3} value={impression} onChange={e => setImpression(e.target.value)} readOnly={!canEdit}
                                        placeholder="Enter clinical impression and conclusion..." />
                                </div>
                            )}

                            {/* Report Delivery Footer (for approved results) */}
                            {isApproved && (canDispatchReports || canApproveReports) && (
                                <div className="rounded-xl border border-[var(--v2-border-card)] bg-[var(--v2-bg-card)] p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 mt-1">
                                    <div className="flex flex-col">
                                        <h4 className="text-[13px] font-bold text-[var(--v2-text-heading)] m-0">Report Ready for Delivery</h4>
                                        <p className="text-[11px] font-medium text-[var(--v2-text-muted)] m-0">This result is fully approved and can be dispatched to the patient.</p>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <Button variant="outline" size="small" onClick={handleGeneratePdf} loading={generatingPdf}
                                            leftIcon={!generatingPdf ? <FileText className="w-full h-full text-blue-600" /> : undefined}>
                                            View Report
                                        </Button>
                                        <Button variant="outline" size="small" onClick={() => setShowEmailModal(true)} leftIcon={<Mail className="w-full h-full text-teal-600" />}>
                                            Email
                                        </Button>
                                        <Button variant="outline" size="small" onClick={handlePrint} loading={printing}
                                            leftIcon={!printing ? <Printer className="w-full h-full text-purple-600" /> : undefined}>
                                            Print
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Keep audit trail attached to bottom of main card */}
                {selectedResult && (
                    <div className="border-t border-[var(--v2-border-subtle)] bg-[var(--v2-bg-surface)] p-2">
                        <ResultAuditTrail result={selectedResult} />
                    </div>
                )}
            </div>

            {/* Modals */}
            {showRejectModal && <RejectModal onConfirm={handleReject} onClose={() => setShowRejectModal(false)} />}
            {showEmailModal && <EmailModal orderId={orderId} onClose={() => setShowEmailModal(false)} />}
            {showCriticalAckModal && (
                <CriticalAckModal
                    paramNames={criticalParams.map(p => p.paramName ?? (p as any).param_name ?? '')}
                    saving={acknowledging}
                    onConfirm={handleAcknowledgeCritical}
                    onClose={() => setShowCriticalAckModal(false)}
                />
            )}
            {trendParam && (
                <ParameterTrendChart
                    mobile={orderMobile}
                    testId={selectedTestId}
                    paramName={trendParam}
                    currentOrderId={orderId}
                    onClose={() => setTrendParam(null)}
                />
            )}

            {/* Test Selection Modal */}
            <Dialog
                isOpen={isSelectTestModalOpen}
                onClose={() => setIsSelectTestModalOpen(false)}
                title="Select Test from Catalog"
                description="Choose a test to begin recording new findings. Only tests available for this order are shown."
                size="small"
            >
                <div className="py-2">
                    <Select
                        value=""
                        onChange={(val) => {
                            if (!val) return;
                            setIsSelectTestModalOpen(false);
                            setSelectedResult(null);
                            setIsNewEntry(true);
                            setParameters([]);
                            setFindings('');
                            setImpression('');
                            setCriticalAckDoneLocal(false);
                            handleTestSelect(val);
                        }}
                        options={[{ value: '', label: '— Select a Test —' }, ...testOptions]}
                        placeholder="Select a Test"
                        className="w-full"
                        size="large"
                    />
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <Button variant="outline" onClick={() => setIsSelectTestModalOpen(false)}>Cancel</Button>
                </div>
            </Dialog>
        </>
    );
};

export default TestProcessing;
